<template>
  <div class="payment-request-action-column">
    <WdsMenu z-index="1000" placement="bottom-end">
      <template #trigger>
        <WdsButtonGhost :label="$t('More options')" data-test="payment-request-actions-menu">
          <WdsIcon name="overflow-menu-horizontal" />
        </WdsButtonGhost>
      </template>

      <WdsMenuOption data-test="see-worker-details-option" @click="seeWorkerDetails">
        <WdsStack direction-row align-center gap-sm>
          <WdsIcon name="person" :size="16" />
          {{ $t('See worker details') }}
        </WdsStack>
      </WdsMenuOption>

      <WdsMenuOption
        data-test="move-to-processing-option"
        :disabled="batchLoading"
        @click="exportBatchAndMoveToProcessing"
      >
        <WdsStack direction-row align-center gap-sm>
          <WdsIcon v-if="!batchLoading" name="download" :size="16" />
          <WdsSpinner v-else :size="16" />
          {{ batchLoading ? $t('Creating batch...') : $t('Export batch and move to processing') }}
        </WdsStack>
      </WdsMenuOption>
    </WdsMenu>
  </div>
</template>

<script lang="ts" setup>
import {
  WdsButtonGhost,
  WdsIcon,
  WdsMenu,
  WdsMenuOption,
  WdsSpinner,
  WdsStack,
} from '@worksome/wds-components'
import { useMutation } from '@vue/apollo-composable'
import { computed } from 'vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { useToast } from '@/composables/useToast'
import { useTablePanels } from '@/components/GraphqlDataTable/useTablePanels'
import { CreateBatchMutation } from '@/components/GraphQL/mutations/Batch/CreateBatchMutation'
import { RunBatchActionMutation } from '@/components/GraphQL/mutations/Batch/RunBatchActionMutation'
import { BatchActionType, BatchType } from '@/gql/graphql'
import { downloadFromUrls } from '@/components/PaymentRequests/Partner/composables/batchActions'
import { FragmentType, graphql, useFragment } from '@/gql'

const { openPartnerWorkerDetailPanel } = useTablePanels()

const PaymentRequestActionColumnToBePayrolled = graphql(`
  fragment PaymentRequestActionColumnToBePayrolled on PaymentRequest {
    id
    worker {
      id
    }
    hire {
      employment {
        id
      }
    }
  }
`)

const props = defineProps<{
  row: FragmentType<typeof PaymentRequestActionColumnToBePayrolled>
}>()

const column = computed(() => useFragment(PaymentRequestActionColumnToBePayrolled, props.row))

const toast = useToast()

const emit = defineEmits<{
  'payment-request-updated': []
}>()

const { mutate: createBatch, loading: batchLoading } = useMutation(CreateBatchMutation, {
  clientId: 'publicApi',
})
const { mutate: runBatchAction } = useMutation(RunBatchActionMutation, {
  clientId: 'publicApi',
})

const seeWorkerDetails = () => {
  openPartnerWorkerDetailPanel({
    workerId: column.value.worker.id,
    employmentId: column.value.hire?.employment?.id,
  })
}

const exportBatchAndMoveToProcessing = async () => {
  try {
    const result = await createBatch({
      input: {
        account: currentAccount.globalId,
        type: BatchType.PartnerPaymentRequests,
        items: [column.value.id],
      },
    })

    const newBatchId = result?.data?.createBatch?.id
    if (newBatchId) {
      const exportRes = await runBatchAction({
        input: {
          batch: newBatchId,
          action: BatchActionType.Export,
          deleteIfEmptied: false,
        },
      })
      const url = exportRes?.data?.runBatchAction?.resultUrl
      await downloadFromUrls([url])
    }

    toast.success($t('Successfully created batch and moved to processing'))
    emit('payment-request-updated')
  } catch (error) {
    toast.error($t('Failed to create batch. Please try again.'))
  }
}
</script>

<style scoped lang="scss">
.payment-request-action-column {
  :deep(.wds-menu) {
    width: max-content;
  }
}
</style>
